import { RoomProvider, useSelf } from '@liveblocks/react'
import { invariant } from '@seen/core/invariant'
import type { BasePlan } from '@seen/core/models/care-plans/BasePlan'
import { CarePlanState } from '@seen/core/models/care-plans/constants'
import { parseDescriptionQuestionnaireUrls } from '@seen/core/models/care-plans/demographics'
import { GoalCarePlan } from '@seen/core/models/care-plans/GoalCarePlan'
import type { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { Intervention } from '@seen/core/models/care-plans/intervention'
import {
  countConditionInCarePlans,
  deleteCarePlanCondition,
} from '@seen/core/models/care-plans/transactions'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { pendingDecisionCount } from '@seen/core/models/compositionNote'
import { ExtendedCondition } from '@seen/core/models/conditions/ExtendedCondition'
import {
  Participant,
  type ParticipantReferenceString,
} from '@seen/core/models/patients/participant'
import { SignatureTask } from '@seen/core/models/tasks/signature/signatureTask'
import { referenceFromReferenceString } from '@seen/core/references'
import {
  getFilenameFromContentDisposition,
  saveBlobWithName,
} from '@seen/core/utils'

import { Text } from '@mantine/core'
import { resolveId } from '@medplum/core'
import type { CarePlan, Patient, Reference } from '@medplum/fhirtypes'
import { Button, cn } from '@seen/ui'
import { Badge, IconUser } from '@seen/ui'
import {
  IconChecks,
  IconChevronDown,
  IconFileExport,
  IconHistory,
  IconSignature,
  IconTrash,
} from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import type { Editor } from '@tiptap/react'
import { compact, orderBy } from 'lodash-es'
import {
  type JSX,
  type MutableRefObject,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { CarePlanGoal } from '#src/components/care-plan/care-plan-goal/CarePlanGoal.tsx'
import { CarePlanActivitiesDrawer } from '#src/components/care-plan/CarePlanActivitiesDrawer.tsx'
import { CarePlanDemographicsProvider } from '#src/components/care-plan/CarePlanDemographicsContext.tsx'
import { CarePlanHeader } from '#src/components/care-plan/CarePlanHeader.tsx'
import { CarePlanPatientDetailsDisplay } from '#src/components/care-plan/CarePlanPatientDetailsDisplay.tsx'
import { InterventionDrawer } from '#src/components/care-plan/interventions/InterventionDrawer.tsx'
import { useCarePlanData } from '#src/components/care-plan/useCarePlanData.ts'
import {
  CollaborativeEditor,
  type DeferredRevealProps,
} from '#src/components/CollaborativeEditor.tsx'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Card } from '#src/components/core/Card.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogButton } from '#src/components/core/dialog/DialogButton.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Disclosure } from '#src/components/core/disclosure/Disclosure.tsx'
import { DisclosureContent } from '#src/components/core/disclosure/DisclosureContent.tsx'
import { DisclosureTrigger } from '#src/components/core/disclosure/DisclosureTrigger.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { VirtualizedList } from '#src/components/core/VirtualizedList.tsx'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FilterEditor } from '#src/components/filter/FilterEditor.tsx'
import { useFilteredData } from '#src/components/filter/useFilteredData.ts'
import { HoverCreatedOn } from '#src/components/HoverCreatedOn.tsx'
import { IconCondition } from '#src/components/icons/IconCondition.tsx'
import { IconInterventionStatus } from '#src/components/icons/IconInterventionStatus.tsx'
import { IconUserPlaceholder } from '#src/components/icons/IconUserPlaceholder.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { ParticipantOption } from '#src/components/options/ParticipantOption.tsx'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import { PlaceholderEditor } from '#src/components/PlaceholderEditor.tsx'
import { StagedSuggestionsBanner } from '#src/components/rich-text-editor/StagedSuggestionsBanner.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import '@liveblocks/react-tiptap/styles.css'
import '@liveblocks/react-ui/styles.css'
import { useAIAssistantStore } from '#src/hooks/useAIAssistantStore.ts'
import { useConfirmWithAnnotationStore } from '#src/hooks/useConfirmWithAnnotationStore.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDeferredSuggestionReveal } from '#src/hooks/useDeferredSuggestionReveal.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useParticipantsActive } from '#src/hooks/useParticipantsActive.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'
import { useSuggestionPersistence } from '#src/hooks/useSuggestionPersistence.ts'

// onDelete replaces leaving the page once its care plan is deleted (an
// embedding host closes its own view).
export function CarePlanDisplayPage({
  onInterventionClose,
  onDelete,
}: {
  onInterventionClose?: () => void
  onDelete?: () => void
}): JSX.Element | null {
  const navigate = useNavigate()
  const { patientId, carePlanId } = useParams()
  const practitionerRef = useCurrentPractitioner().reference
  const isReadOnly = useIsReadOnly()

  const patientRef = useMemo<Reference<Patient>>(
    () => ({ reference: `Patient/${patientId}` }),
    [patientId]
  )
  const carePlanRef = useMemo<Reference<CarePlan>>(
    () => ({ reference: `CarePlan/${carePlanId}` }),
    [carePlanId]
  )

  /* Establish the AI chat edit-target so staged proposals on the care-plan
   * description persist. The chat write path keys off this ref, which MUST match
   * the editor's `aiMetadata.resourceRef` (`CarePlan/${carePlanId}`) for the
   * read (editor) and write (chat) sides to resolve the same staging Basic. */
  const setResourceContext = useAIAssistantStore(
    (state) => state.setResourceContext
  )
  const clearResourceContext = useAIAssistantStore(
    (state) => state.clearResourceContext
  )
  useEffect(() => {
    if (carePlanId) {
      setResourceContext(`CarePlan/${carePlanId}`)
    }
    return () => {
      clearResourceContext()
    }
  }, [carePlanId, setResourceContext, clearResourceContext])

  const allParticipantGoalCarePlansQuery = useSearch(
    GoalCarePlan,
    GoalCarePlan.byParticipant(patientRef)
  )
  const allParticipantGoalCarePlans = useMemo(
    () => allParticipantGoalCarePlansQuery.data ?? [],
    [allParticipantGoalCarePlansQuery.data]
  )

  const {
    carePlan,
    goalCarePlans,
    signatures,
    interventionsByGoalCarePlan,
    isLoading,
    demographics,
  } = useCarePlanData(patientRef, carePlanRef)

  const descriptionQuestionnaireUrls = parseDescriptionQuestionnaireUrls(
    carePlan?.description
  )

  const seenApi = useSeenApi().seenApi
  const exportPdfMutation = useMutation<unknown, unknown, InitialCarePlan>({
    mutationFn: async (cp) => {
      const response = await seenApi.post(
        `/pdf/care-plan/${cp.id}`,
        {},
        { responseType: 'blob' }
      )
      const filename = getFilenameFromContentDisposition(
        response.headers['content-disposition']
      )
      saveBlobWithName(
        response.data,
        filename ?? `${cp.title ?? 'care-plan'}.pdf`
      )
    },
    onMutate: (cp) => {
      toast.loading(`Exporting care plan…`, {
        id: `export-care-plan-${cp.id}`,
      })
    },
    onSettled: (_, __, cp) => {
      toast.dismiss(`export-care-plan-${cp.id}`)
    },
    onError: () => {
      toast.error('Failed to export care plan')
    },
  })

  const { mutate: createGoalCarePlan } = useCreateMutation<GoalCarePlan>()

  const { mutate: patchCarePlan } = usePatchMutation<BasePlan>()
  const { mutate: patchGoalCarePlan } = usePatchMutation<GoalCarePlan>()
  const { mutate: mutateTransaction } = useTransactionMutation()
  const { open: confirmWithAnnotation } = useConfirmWithAnnotationStore()

  const handleMoveGoal = useCallback(
    (goalCarePlan: GoalCarePlan, direction: 'up' | 'down') => {
      patchGoalCarePlan({
        instance: goalCarePlan,
        mutateFn: (goalCarePlan) => {
          if (direction === 'up') {
            goalCarePlan.moveUp(goalCarePlans)
          } else {
            goalCarePlan.moveDown(goalCarePlans)
          }
        },
      })
    },
    [goalCarePlans, patchGoalCarePlan]
  )

  const deleteCarePlan = useCallback(() => {
    confirmWithAnnotation({
      title: 'Delete Care Plan',
      description:
        'Are you sure you want to delete this care plan? This action cannot be undone.',
      onConfirm: (note: string) => {
        if (!carePlan) {
          return
        }
        mutateTransaction(
          async (transaction) => {
            transaction.patch(carePlan, (cp) => {
              cp.status = 'entered-in-error'
            })

            const trimmedNote = note?.trim()
            if (trimmedNote) {
              transaction.create(
                new CommentCommunication({
                  partOf: [carePlan.reference],
                  message: `Deleted: ${trimmedNote}`,
                  sender: practitionerRef,
                  subject: carePlan.patientSubject,
                })
              )
            }
          },
          {
            onSuccess: () => {
              if (onDelete) {
                onDelete()
              } else {
                void navigate('..')
              }
            },
          }
        )
      },
    })
  }, [
    carePlan,
    mutateTransaction,
    confirmWithAnnotation,
    navigate,
    onDelete,
    practitionerRef,
  ])

  const handleDeleteCondition = useCallback(
    (carePlan: BasePlan, condition: ExtendedCondition) => {
      if (
        countConditionInCarePlans(allParticipantGoalCarePlans, condition) <= 1
      ) {
        confirmWithAnnotation({
          title: 'Delete Condition',
          description: 'Are you sure you want to delete this condition?',
          onConfirm: (note: string) => {
            mutateTransaction(async (transaction) => {
              deleteCarePlanCondition(transaction, {
                carePlans: goalCarePlans,
                condition,
              })

              const trimmedNote = note?.trim()
              if (trimmedNote) {
                transaction.create(
                  new CommentCommunication({
                    partOf: [carePlan.reference],
                    message: `Deleted condition (${condition.title}): ${trimmedNote}`,
                    sender: practitionerRef,
                    subject: carePlan.patientSubject,
                  })
                )
              }
            })
          },
        })
      } else {
        patchCarePlan({
          instance: carePlan,
          mutateFn: (carePlan) => {
            carePlan.removeCondition(condition.reference)
          },
        })
      }
    },
    [
      goalCarePlans,
      allParticipantGoalCarePlans,
      confirmWithAnnotation,
      mutateTransaction,
      patchCarePlan,
      practitionerRef,
    ]
  )

  if (isLoading) {
    return <Loader />
  }

  if (carePlan == null) {
    return (
      <Text>
        {carePlanId == null
          ? 'There are no active care plans for this participant. Create one now.'
          : 'We ran into an error loading the selected care plan.'}
      </Text>
    )
  }

  const signedSnapshotId = resolveId(carePlan.signedSnapshots.at(-1))
  const shouldShowSignedSnapshotButton =
    signedSnapshotId != null &&
    (carePlan.state === CarePlanState.Active ||
      carePlan.state === CarePlanState.Closed)
  const showParticipantSummary = !carePlan.noParticipantSummary

  return (
    <>
      <div className="flex flex-col gap-4">
        <CarePlanHeader carePlan={carePlan} activitiesPath="./activities" />
        <div className="flex flex-wrap gap-4">
          {shouldShowSignedSnapshotButton && (
            <Button
              variant="outline"
              className="flex items-center gap-2"
              asChild
            >
              <Link to={`../history/${signedSnapshotId}`}>
                <IconChecks className="size-4" />
                View Signed Snapshot
              </Link>
            </Button>
          )}
          <Button
            onClick={() => navigate(`./signatures`)}
            variant="outline"
            className="flex items-center gap-2"
          >
            <IconSignature className="size-4" />
            {signatures.length === 0
              ? 'Request Signatures'
              : `View Signatures ${SignatureTask.completedRatio(signatures)}`}
          </Button>
          <Button
            onClick={() => exportPdfMutation.mutate(carePlan)}
            variant="outline"
            className="flex items-center gap-2"
            disabled={exportPdfMutation.isPending}
          >
            <IconFileExport className="size-4" />
            {exportPdfMutation.isPending ? 'Exporting…' : 'Export Care Plan'}
          </Button>
          <Button
            onClick={() => navigate(`/Patient/${patientId}/care-plans/history`)}
            variant="outline"
            className="flex items-center gap-2"
          >
            <IconHistory className="size-4" />
            View History
          </Button>
          {!isReadOnly && carePlan.isDraft && (
            <Button
              onClick={deleteCarePlan}
              variant="outline"
              className="flex items-center gap-2"
            >
              <IconTrash className="size-4" />
              Delete
            </Button>
          )}
        </div>
        <CarePlanDemographicsProvider value={demographics}>
          <CarePlanDescriptionCard carePlan={carePlan} />
        </CarePlanDemographicsProvider>
        {showParticipantSummary && (
          <Card className="p-0">
            <Disclosure>
              <DisclosureTrigger>
                <Badge variant="secondary" className="justify-self-start">
                  Participant Summary
                </Badge>
              </DisclosureTrigger>
              <DisclosureContent>
                <CarePlanPatientDetailsDisplay
                  patient={carePlan.patientSubject}
                  goalCarePlans={goalCarePlans}
                  demographics={demographics}
                  descriptionQuestionnaireUrls={descriptionQuestionnaireUrls}
                />
              </DisclosureContent>
            </Disclosure>
          </Card>
        )}
        {interventionsByGoalCarePlan.map(
          ({ goalCarePlan, interventions }, index) => (
            <CarePlanGoal
              key={goalCarePlan.id}
              goalNumber={index + 1}
              goalCarePlan={goalCarePlan}
              onMoveUp={() => handleMoveGoal(goalCarePlan, 'up')}
              onMoveDown={() => handleMoveGoal(goalCarePlan, 'down')}
              isFirst={index === 0}
              isLast={index === goalCarePlans.length - 1}
              onDeleteCondition={handleDeleteCondition}
              interventions={interventions}
              allGoalCarePlans={goalCarePlans}
            />
          )
        )}
        {!isReadOnly && (
          <div className="flex justify-center gap-4">
            <Button
              variant="outline"
              onClick={() => {
                createGoalCarePlan(
                  new GoalCarePlan({
                    subject: carePlan.patientSubject,
                    initialCarePlan: carePlan.reference,
                    order: goalCarePlans.length + 1,
                  })
                )
              }}
            >
              Add Goal
            </Button>
            <AddGoalFromTemplateButton
              participant={`Patient/${patientId}`}
              carePlan={carePlan.reference.reference}
              numCarePlans={goalCarePlans.length}
            />
          </div>
        )}
      </div>
      <CarePlanActivitiesDrawer carePlan={carePlan} />
      <InterventionDrawer
        interventionsByGoalCarePlan={interventionsByGoalCarePlan}
        onClose={onInterventionClose}
      />
    </>
  )
}

function AddGoalFromTemplateButton({
  participant,
  carePlan,
  numCarePlans,
}: {
  participant: ParticipantReferenceString
  carePlan: string
  numCarePlans: number
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogButton variant="outline">Add Goal from Template</DialogButton>
      <DialogDisplay
        notScrollable
        onFocusCapture={(e) => e.stopPropagation()} // Prevent the Filter button from automatically showing its tooltip on Dialog open
      >
        <AddGoalFromTemplateContent
          participant={participant}
          carePlan={carePlan}
          numCarePlans={numCarePlans}
          onAdd={() => setIsOpen(false)}
        />
      </DialogDisplay>
    </Dialog>
  )
}

function AddGoalFromTemplateContent({
  participant: participantReferenceString,
  carePlan,
  numCarePlans,
  onAdd,
}: {
  participant: ParticipantReferenceString
  carePlan: string
  numCarePlans: number
  onAdd: () => void
}): ReactNode {
  const {
    data: allGoalCarePlans = EMPTY_GOAL_CARE_PLANS,
    isLoading: isLoadingAllGoalCarePlans,
  } = useSearchResourcePages(GoalCarePlan, GoalCarePlan.queryAll())

  const {
    data: allConditions = EMPTY_CONDITIONS,
    isLoading: isLoadingAllConditions,
  } = useSearchResourcePages(ExtendedCondition, ExtendedCondition.queryBase)

  const conditionsByReferenceString = useMemo(
    () =>
      allConditions.reduce<Record<string, ExtendedCondition>>(
        (acc, condition) => {
          acc[condition.reference.reference] = condition
          return acc
        },
        {}
      ),
    [allConditions]
  )

  const participants = useParticipantsActive()
  const participantOptions = useMemo(
    () =>
      participants.map((p) => ({
        label: <ParticipantOption participant={p.reference} />,
        value: p.reference.reference,
        icon: <Avatar profile={p.reference} size={3.5} />,
        keywords: p.searchKeywords,
      })),
    [participants]
  )

  const participantConditions = useMemo(
    () =>
      compact(
        allConditions.filter(
          (condition) =>
            condition.subject?.reference === participantReferenceString
        )
      ),
    [allConditions, participantReferenceString]
  )

  const numGoalCarePlansByParticipantCondition = useMemo(
    () =>
      allGoalCarePlans.reduce<Record<string, number>>((acc, gcp) => {
        const gcpConditions = compact(
          gcp.conditions.map((c) => {
            invariant(c.reference, 'Condition reference is required')
            return conditionsByReferenceString[c.reference]
          })
        )
        const relatedParticipantConditions = getRelatedConditions({
          fromConditions: participantConditions,
          relatedToConditions: gcpConditions,
        })
        relatedParticipantConditions.forEach((c) => {
          acc[c.reference.reference] = (acc[c.reference.reference] ?? 0) + 1
        })
        return acc
      }, {}),
    [allGoalCarePlans, participantConditions, conditionsByReferenceString]
  )

  const [filters, setFilters] = useState<Filter[]>([])

  const filterOptions = useMemo<FilterConfigMap<GoalCarePlan>>(
    () => ({
      condition: {
        label: 'Condition',
        icon: <IconCondition className="size-4" />,
        type: 'multi-select-array',
        options:
          orderBy(
            participantConditions,
            (c) =>
              numGoalCarePlansByParticipantCondition[c.reference.reference] ??
              0,
            'desc'
          ).map((condition) => ({
            label: (
              <span>
                {condition.title}
                {!!numGoalCarePlansByParticipantCondition[
                  condition.reference.reference
                ] && (
                  <span className="pl-2 text-xs text-foreground-secondary">
                    {
                      numGoalCarePlansByParticipantCondition[
                        condition.reference.reference
                      ]
                    }{' '}
                    goals
                  </span>
                )}
              </span>
            ),
            value: condition.reference.reference,
            keywords: [condition.title],
          })) ?? [],
        valueFn: (gcp) => {
          const gcpConditions = compact(
            gcp.conditions.map((c) => {
              invariant(c.reference, 'Condition reference is required')
              return conditionsByReferenceString[c.reference]
            })
          )
          const matchingParticipantConditions = getRelatedConditions({
            fromConditions: participantConditions,
            relatedToConditions: gcpConditions,
          })
          return matchingParticipantConditions.map((c) => c.reference.reference)
        },
      },
      participant: {
        label: 'Participant',
        icon: <IconUser className="size-4" />,
        type: 'multi-select',
        options: participantOptions,
        valueFn: (gcp) => gcp.patientSubject.reference ?? '',
      },
    }),
    [
      participantConditions,
      numGoalCarePlansByParticipantCondition,
      conditionsByReferenceString,
      participantOptions,
    ]
  )

  const filteredGoalCarePlans = useFilteredData(
    allGoalCarePlans,
    filterOptions,
    filters
  )

  const resultGoalCarePlans = useMemo(
    () => orderBy(filteredGoalCarePlans, 'created', 'desc'),
    [filteredGoalCarePlans]
  )

  const transactionMutate = useTransactionMutation().mutate

  const rows = resultGoalCarePlans.map((goalCarePlan, idx) => (
    <GoalCarePlanRow
      key={goalCarePlan.id}
      goalCarePlan={goalCarePlan}
      hideBorder={idx === resultGoalCarePlans.length - 1}
      onSelect={(interventions) => {
        transactionMutate((transaction) => {
          const gcpConditionSet = new Set(
            compact(
              goalCarePlan.conditions.map(
                (gcpCondition) => gcpCondition.reference
              )
            )
          )
          const matchingParticipantConditions = getRelatedConditions({
            fromConditions: participantConditions,
            relatedToConditions: allConditions.filter((condition) =>
              gcpConditionSet.has(condition.reference.reference)
            ),
          })
          const newGoalCarePlan = transaction.create(
            new GoalCarePlan({
              subject: referenceFromReferenceString(participantReferenceString),
              initialCarePlan: referenceFromReferenceString(carePlan),
              order: numCarePlans + 1,
              title: goalCarePlan.title,
              description: goalCarePlan.description,
              addresses: matchingParticipantConditions.map((c) => c.reference),
            })
          )
          interventions.forEach((intervention, idx) => {
            const newIntervention = new Intervention({
              goalCarePlan: newGoalCarePlan,
              subject: referenceFromReferenceString(participantReferenceString),
              title: intervention.title,
              description: intervention.description,
              order: idx + 1,
            })
            newIntervention.author = intervention.author
            newIntervention.idtRoles = intervention.idtRoles
            newIntervention.activityReferences = intervention.activityReferences
            transaction.create(newIntervention)
          })
        })
        onAdd()
      }}
    />
  ))

  const isLoading = isLoadingAllConditions || isLoadingAllGoalCarePlans

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-4 border-b p-6 pr-14">
        <DialogTitle>Add Goal from Template</DialogTitle>
      </div>
      <div className="border-b px-6 py-2">
        <FilterEditor
          filterOptions={filterOptions}
          filters={filters}
          setFilters={setFilters}
        />
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        {isLoading && (
          <div className="h-screen">
            <Loader />
          </div>
        )}
        {!isLoading && !!rows.length && (
          <VirtualizedList
            rows={rows}
            estimateSize={estimateGoalCarePlanHeight}
            scrollRestorationId={null}
            dynamic
          />
        )}
        {!isLoading && !rows.length && (
          <div className="flex h-screen items-center justify-center p-6">
            <p className="text-foreground-secondary">No matching goals found</p>
          </div>
        )}
      </div>
    </div>
  )
}

const GoalCarePlanRow = memo(function GoalCarePlanRow({
  goalCarePlan,
  onSelect,
  hideBorder,
}: {
  goalCarePlan: GoalCarePlan
  onSelect: (interventions: Intervention[]) => void
  hideBorder?: boolean
}): ReactNode {
  const { data: interventions } = useSearch(
    Intervention,
    Intervention.byGoalCarePlan({
      goalCarePlan: goalCarePlan.reference,
    })
  )
  return (
    <div
      key={goalCarePlan.id}
      className={cn('flex flex-col gap-4 px-6 py-4', !hideBorder && 'border-b')}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1">
          <ParticipantTaskRowBadge participant={goalCarePlan.patientSubject} />
          {!!goalCarePlan.conditions.length &&
            goalCarePlan.conditions.map((condition) => (
              <ConditionBadge
                key={condition.id}
                condition={condition.reference}
              />
            ))}
        </div>
        <div className="shrink-0">
          {goalCarePlan.created && (
            <HoverCreatedOn createdOn={goalCarePlan.created} />
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <RichText value={goalCarePlan.title} className="text-sm" />
        {goalCarePlan.description && (
          <RichText value={goalCarePlan.description} className="text-xs" />
        )}
      </div>
      <div className="flex flex-col gap-2">
        {!!interventions?.length &&
          interventions.map((intervention) => (
            <div
              key={intervention.id}
              className={cn(
                'flex items-start gap-2',
                intervention.isInactive && 'text-foreground-secondary'
              )}
            >
              <div className="py-0.5">
                <IconInterventionStatus
                  status={intervention.status}
                  className={cn(
                    intervention.isInactive && 'text-foreground-secondary'
                  )}
                />
              </div>
              <div className="flex-1">
                <RichText
                  value={intervention.description}
                  className="text-sm"
                />
              </div>
              {intervention.author && (
                <PractitionerNameplate
                  practitioner={intervention.author}
                  className="flex-row-reverse gap-1.25 text-xs"
                />
              )}
              {!intervention.author && (
                <IconUserPlaceholder className="text-foreground-secondary" />
              )}
            </div>
          ))}
      </div>
      <div className="flex items-center justify-end">
        <Button
          variant="outline"
          onClick={() => {
            onSelect(interventions ?? [])
          }}
        >
          Use as template
        </Button>
      </div>
    </div>
  )
})

function ConditionBadge({
  condition: conditionReferenceString,
}: {
  condition: string | undefined
}): ReactNode {
  const { data: condition } = useRead(
    ExtendedCondition,
    conditionReferenceString
  )
  if (!condition) {
    return null
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge className="select-none" variant="outline">
          {condition.title}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        {condition.title}
        {condition.icd10Code?.code && (
          <span className="ml-1 text-foreground-secondary">
            [{condition.icd10Code.code}]
          </span>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

function estimateGoalCarePlanHeight(): number {
  return 100
}

function getRelatedConditions({
  fromConditions,
  relatedToConditions,
}: {
  fromConditions: ExtendedCondition[]
  relatedToConditions: ExtendedCondition[]
}): ExtendedCondition[] {
  const icd10Codes = new Set(
    compact(relatedToConditions.map((condition) => condition.icd10Code?.code))
  )
  const titles = new Set(
    compact(relatedToConditions.map((condition) => condition.title))
  )
  return compact(
    fromConditions.filter(
      (condition) =>
        (!!condition.icd10Code?.code &&
          icd10Codes.has(condition.icd10Code.code)) ||
        titles.has(condition.title)
    )
  )
}

const EMPTY_CONDITIONS: ExtendedCondition[] = []

const EMPTY_GOAL_CARE_PLANS: GoalCarePlan[] = []

function CarePlanDescription({
  carePlan,
}: {
  carePlan: InitialCarePlan
}): ReactNode {
  // Skip RoomProvider when read-only to avoid opening a Liveblocks connection.
  if (!carePlan.isEditable) {
    return <RichText value={carePlan.description} />
  }
  return <CarePlanDescriptionEditable carePlan={carePlan} />
}

/* Owns the staged-suggestions banner OUTSIDE the Liveblocks room, the way
 * ParticipantVisitNotePage does. Left inside, the banner cannot paint until the
 * room connects and the editor mounts — several seconds during which a note
 * with staged suggestions looks like a note with none. The staging search is
 * independent of collaboration, so hoisting it lets the banner appear as soon
 * as that search settles and show its own loading state meanwhile.
 *
 * The persistence call here subscribes to the SAME author-scoped query key the
 * editor's own call uses, so React Query dedups rather than double-fetching. */
function CarePlanDescriptionEditable({
  carePlan,
}: {
  carePlan: InitialCarePlan
}): ReactNode {
  const resourceRef = carePlan.reference.reference
  const editorRef = useRef<Editor | null>(null)
  const getEditor = useCallback(() => editorRef.current, [])
  const suggestionPersistence = useSuggestionPersistence(resourceRef)
  const {
    revealed,
    revealing,
    revealGen,
    handleReveal,
    handleRevealSettled,
    handleLiveStaged,
    handleHide,
  } = useDeferredSuggestionReveal({
    resourceKey: resourceRef,
    isResourceLoaded: suggestionPersistence.isResourceLoaded,
    hasPersistedPending: suggestionPersistence.hasPersistedPending,
    getEditor,
  })
  const deferredReveal = useMemo<DeferredRevealProps>(
    () => ({
      revealed,
      revealing,
      revealGen,
      onRevealSettled: handleRevealSettled,
      onHide: handleHide,
      onLiveStaged: handleLiveStaged,
    }),
    [
      revealed,
      revealing,
      revealGen,
      handleRevealSettled,
      handleHide,
      handleLiveStaged,
    ]
  )

  return (
    <>
      {suggestionPersistence.hasPersistedPending &&
        (!revealed || revealing) && (
          <StagedSuggestionsBanner
            count={pendingDecisionCount(
              suggestionPersistence.pendingSuggestions
            )}
            onReview={handleReveal}
            revealing={revealing}
            // Clears the patient nav header, as the status bar below does.
            stickyTopClassName="top-16"
          />
        )}
      <RoomProvider id={resourceRef}>
        <CarePlanDescriptionInner
          carePlan={carePlan}
          deferredReveal={deferredReveal}
          editorRef={editorRef}
        />
      </RoomProvider>
    </>
  )
}

function CarePlanDescriptionInner({
  carePlan,
  deferredReveal,
  editorRef,
}: {
  carePlan: InitialCarePlan
  deferredReveal: DeferredRevealProps
  editorRef: MutableRefObject<Editor | null>
}): ReactNode {
  const { data: participant } = useRead(Participant, carePlan.patientSubject)
  const mutate = usePatchMutation<InitialCarePlan>().mutate

  const documentReferenceContextRelated = useMemo(
    () => [carePlan.reference],
    [carePlan.reference]
  )

  const aiMetadata = useMemo(
    () => ({
      id: 'care-plan-description',
      label: 'Care plan description',
      resourceRef: carePlan.reference.reference,
      field: 'description',
      description:
        "Free-text narrative for the participant's PACE care plan. Used by the interdisciplinary team to record goals, interventions, and clinical context. Avoid touching custom widgets (validated tools, encounters); prefer scoped anchored edits over replaceAll.",
    }),
    [carePlan.reference.reference]
  )

  const canWrite = useSelf((me) => me.canWrite)

  const shouldShowLoadedToast = useRef(false)
  useEffect(() => {
    if (canWrite && shouldShowLoadedToast.current) {
      toast.success('Editor loaded. Click again to edit.')
      shouldShowLoadedToast.current = false
    }
  }, [canWrite])

  if (!canWrite) {
    return (
      <PlaceholderEditor
        value={carePlan.description}
        onClick={() => {
          shouldShowLoadedToast.current = true
        }}
      />
    )
  }

  return (
    <CollaborativeEditor
      initialContent={carePlan.description}
      onChange={(sanitizedHtml) => {
        mutate({
          instance: carePlan,
          mutateFn: (cp) => {
            cp.description = sanitizedHtml
          },
        })
      }}
      fileHandlerDocumentReferenceContextRelated={
        documentReferenceContextRelated
      }
      participant={participant}
      aiMetadata={aiMetadata}
      deferredReveal={deferredReveal}
      editorRef={editorRef}
      // The care plan renders in the shared page ScrollArea, which also holds
      // the `sticky top-0 z-50` patient nav header. Pin the proposal bar below
      // that header (its `z-20` would otherwise lose the stacking contest and
      // slide behind it). The visit note keeps the default `top-4` because its
      // editor owns a dedicated scroll viewport with no competing header.
      aiProposalStickyTopClassName="top-16"
    />
  )
}

function CarePlanDescriptionCard({
  carePlan,
}: {
  carePlan: InitialCarePlan
}): ReactNode {
  const [isOpen, setIsOpen] = useState(false)

  const { ref, exceeds } = useExceedsHeight<HTMLDivElement>(156) // 156px is a bit less than the h-40 below

  return (
    <Card
      className={cn(
        'p-0 pb-4 [&:has(>button:first-child:hover)]:bg-accent',
        exceeds && !isOpen && 'hover:bg-accent'
      )}
    >
      {exceeds && (
        <Button
          className="flex w-full items-center justify-between px-4 pt-4"
          onClick={() => {
            setIsOpen(!isOpen)
          }}
          variant="unstyled"
          size="unstyled"
        >
          <Badge variant="secondary" className="justify-self-start">
            Description
          </Badge>
          <IconChevronDown
            className={cn(
              'size-4 shrink-0 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </Button>
      )}
      {!exceeds && (
        <div className="px-4 pt-4">
          <Badge variant="secondary" className="justify-self-start">
            Description
          </Badge>
        </div>
      )}
      <div
        className={cn(
          'relative h-auto',
          exceeds &&
            !isOpen &&
            'h-40 overflow-clip mask-[linear-gradient(white,transparent)]'
        )}
      >
        <div
          ref={ref}
          className="px-4 pt-4 text-sm"
          onFocus={() => {
            setIsOpen(true) // If we don't set this and open is defaulted to false, if the user types text such that exceeds goes from false to true, the editor will close on them unexpectedly.
          }}
          role="none"
        >
          <CarePlanDescription carePlan={carePlan} />
        </div>
        {exceeds && !isOpen && (
          <Button
            className="absolute inset-0 z-10"
            onClick={() => {
              setIsOpen(true)
            }}
            variant="unstyled"
            size="unstyled"
          />
        )}
      </div>
    </Card>
  )
}

function useExceedsHeight<T extends HTMLElement>(threshold: number) {
  const ref = useRef<T | null>(null)
  const [exceeds, setExceeds] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) {
      return
    }

    const checkHeight = () => setExceeds(el.scrollHeight > threshold)

    // Run immediately
    checkHeight()

    const observer = new ResizeObserver(checkHeight)
    observer.observe(el)

    return () => observer.disconnect()
  }, [threshold])

  return { ref, exceeds }
}
